import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Card,
  Divider,
  Page,
  PayTypeToIcon,
  TextField,
} from "@towersystems/roam-common-native";
import { useEffect } from "react";
import { FlatList, Pressable } from "react-native";
import { Colors, Spacings, Text, View } from "react-native-ui-lib";
import { CheckoutRootParamList } from "../../../../types";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { moneyToFormattedString } from "../../../../utilities/money";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { usePaymentControllerProvider } from "../../utils/payment-controller";
import { SelectablePaymentMethodButton } from "../SelectPaymentMethodScreen/components/SelectablePaymentMethodButton";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { onlineOnlyIds } from "../SelectPaymentMethodScreen";

export type SplitPaymentScreenProps = NativeStackScreenProps<
  CheckoutRootParamList,
  "SplitPaymentScreen"
>;

export const SplitPaymentScreen = ({ navigation }: SplitPaymentScreenProps) => {
  const shouldFocus = useHasTransitionEnded(navigation);
  const { sale, selectablePaymentMethods, balance, totals } =
    useCheckoutContextProvider();
  const online = useOnline();
  const { tenderedAmountInput, validTenderedAmount, setTenderedAmountInput } =
    usePaymentControllerProvider();

  useEffect(() => {
    setTenderedAmountInput("");
  }, []);

  // if it is a negative balance, then the only valid option between 0 and the balance
  const isNegativeBalance = balance < 0;

  const tenderedAmountInputAsNumber = Number(tenderedAmountInput);

  const isZeroTendered = tenderedAmountInputAsNumber === 0;

  // cant over tender a negative balance
  const overTendered =
    validTenderedAmount &&
    (isNegativeBalance
      ? tenderedAmountInputAsNumber > 0 ||
        tenderedAmountInputAsNumber < balance ||
        tenderedAmountInputAsNumber < totals.totalPrice
      : tenderedAmountInputAsNumber > balance ||
        tenderedAmountInputAsNumber < 0 ||
        tenderedAmountInputAsNumber > totals.totalPrice);

  const failPositiveBalanceCheck =
    !isNegativeBalance && tenderedAmountInputAsNumber < 0;

  const failNegativeBalanceCheck =
    isNegativeBalance &&
    (tenderedAmountInputAsNumber > 0 || tenderedAmountInputAsNumber < balance);

  const renderItem = ({ item }: { item: PaymentMethod }) => {
    const isCash = item.payType.id === "1";

    // why were we allowing for cash to over tender here?
    // removed 15/04/2025
    const disabled =
      (onlineOnlyIds.includes(item.payType.id) && !online) ||
      !validTenderedAmount ||
      overTendered ||
      failNegativeBalanceCheck ||
      failPositiveBalanceCheck ||
      (isCash == false && isZeroTendered);

    return (
      <SelectablePaymentMethodButton
        onPress={() =>
          navigation.navigate("PaymentRootScreen", {
            screen: "PaymentScreen",
            paymentMethodId: item.id,
            tenderedAmount: tenderedAmountInputAsNumber,
            minTenderAmount: tenderedAmountInputAsNumber,
          })
        }
        disabled={disabled}
        label={item.name}
        icon={<PayTypeToIcon payType={item.payType.id} />}
      />
    );
  };

  const secondaryTitle =
    sale.payments.length >= 1 ? (
      <Pressable
        onPress={() => navigation.navigate("CheckoutPaymentHistoryScreen")}
      >
        <Text style={{ color: Colors.action, fontSize: 22 }}>{`${
          sale.payments.length
        } payments (${moneyToFormattedString(sale.paymentsTotal)})`}</Text>
      </Pressable>
    ) : undefined;

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={`Split Payment ${sale.payments.length + 1}`}
      secondaryTitle={secondaryTitle}
    >
      <View>
        <View style={{ marginBottom: Spacings.s5 }}>
          <Text
            style={{
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s4,
            }}
          >
            Enter payment amount
          </Text>
          <View>
            <TextField
              onChangeText={setTenderedAmountInput}
              value={tenderedAmountInput}
              autoFocus={shouldFocus}
              keyboardType="numeric"
              formatterType={validTenderedAmount ? "money" : undefined}
              validationMessage={
                tenderedAmountInput.length >= 1 && !validTenderedAmount
                  ? "Invalid amount"
                  : undefined ?? overTendered
                  ? "Tender limit exceeded"
                  : undefined
              }
            />
          </View>
        </View>
        <Card style={{ padding: 0 }}>
          <FlatList
            ItemSeparatorComponent={() => <Divider marginVertical={0} />}
            renderItem={renderItem}
            data={selectablePaymentMethods}
            scrollEnabled={false}
          ></FlatList>
        </Card>
      </View>
    </Page>
  );
};
